import React, { useState } from 'react';
import { M, Flex, Checkbox } from 'design-system-web';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { CheckboxGroup } from 'components';
import styles from './BulkRemoveCompanyRoleModal.scss';

type Props = {
  isOpen: boolean;
  bulkSelectedUsers: {
    id: string;
    name: string;
    email: string;
    companyRoles: Array<{
      companyId: string;
      companyName: string;
      roles: Array<{ id: string; name: string }>;
    }>;
  }[];
  onClose: () => void;
  onSuccess?: () => void;
};

const TRANSLATION_KEYS = {
  modalTitle:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.title',
  removeButtonLabel:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.removeButton.label',
  cancelButtonLabel:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.cancelButton.label',
  promptText:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.prompt.text',
  rolesListSelectAll:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.modal.rolesList.selectAll',
  toastText:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.success.toast.text',
  errorMessage:
    'web.admin.channel.users.all.table.bulkRemoveCompanyRole.failed.alert.text',
};

export const BulkRemoveCompanyRoleModal = ({
  isOpen,
  bulkSelectedUsers,
  onClose,
  onSuccess,
}: Props) => {
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();

  // Collect all unique roles from all selected users
  const roles = bulkSelectedUsers
    .flatMap(({ companyRoles }) => companyRoles)
    .flatMap(({ roles }) => roles)
    .reduce((rolesMap, role) => {
      if (!rolesMap.has(role.id)) {
        rolesMap.set(role.id, { label: role.name, value: role.id });
      }

      return rolesMap;
    }, new Map<string, { label: string; value: string }>());

  const closeModal = () => {
    onClose();
    setSelectedRoleIds([]);
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      // TODO: Replace with actual mutation when backend is ready
      // For now, simulate a simple success response
      await new Promise<void>(resolve => {
        setTimeout(() => {
          resolve();
        }, 500);
      });

      // Show success message
      window.Toast.show(t(TRANSLATION_KEYS.toastText), 5000);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      await window.Alert.alert({
        title: t(TRANSLATION_KEYS.errorMessage),
        error: err,
      });
    }

    setLoading(false);
    closeModal();
  };

  const handleClose = () => {
    closeModal();
  };

  const handleSelectAll = (allRolesSelected: boolean) => {
    if (allRolesSelected) {
      setSelectedRoleIds([]);
    } else {
      setSelectedRoleIds(Array.from(roles.keys()));
    }
  };

  const bulkSelectedUsersCount = bulkSelectedUsers.length;
  const selectedRoleIdsCount = selectedRoleIds.length;
  const allRolesSelected = selectedRoleIds.length === roles.size;
  const roleOptions = Array.from(roles.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={handleClose}
      handleConfirm={handleConfirm}
      title={t(TRANSLATION_KEYS.modalTitle)}
      confirmText={t(TRANSLATION_KEYS.removeButtonLabel, {
        count: selectedRoleIdsCount,
      })}
      cancelText={t(TRANSLATION_KEYS.cancelButtonLabel)}
      loading={loading}
      confirmActionDisabled={!selectedRoleIdsCount || loading}
      cancelActionDisabled={loading}
      isFullWidth
    >
      <M>
        <Trans
          components={[<b />]}
          i18nKey={TRANSLATION_KEYS.promptText}
          count={bulkSelectedUsersCount}
          values={{
            count: bulkSelectedUsersCount,
          }}
        />
      </M>
      <Flex direction="column" gap={1} className={styles.rolesChecklist}>
        <Checkbox
          onChange={handleSelectAll}
          value={allRolesSelected}
          text={t(TRANSLATION_KEYS.rolesListSelectAll)}
          selected={allRolesSelected}
        />
        <hr className={styles.divider} />
        <CheckboxGroup
          schema={{ id: 'value', text: 'label' }}
          items={roleOptions}
          selected={selectedRoleIds}
          name="roles"
          onChange={setSelectedRoleIds}
          disabled={loading}
        />
      </Flex>
    </ConfirmationModal>
  );
};

